import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit3,
  Maximize2,
  Play,
  Eye,
  Save,
  Share2,
  Presentation } from
'lucide-react';
import { useToast } from '@/hooks/use-toast';
import SlideRenderer from './SlideRenderer';
import FullscreenPresentationView from './FullscreenPresentationView';

interface ProcessedSlide {
  id: string;
  title: string;
  content: string | string[];
  layoutType: string;
  processedContent: any;
  designApplied: boolean;
  type: 'title' | 'content' | 'image' | 'list' | 'conclusion' | 'quote' | 'comparison' | 'image-text';
  notes?: string;
  imageUrl?: string;
  imageAlt?: string;
}

interface Template {
  id: string;
  name: string;
  preview: {
    titleSlide: {
      backgroundColor: string;
      textColor: string;
      accentColor: string;
    };
    contentSlide: {
      backgroundColor: string;
      textColor: string;
      accentColor: string;
    };
    fonts: {
      heading: string;
      body: string;
    };
  };
  colorScheme: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
  };
}

interface EnhancedPresentationViewProps {
  slides: ProcessedSlide[];
  template: Template;
  title: string;
  onEdit?: () => void;
  onDownload?: () => void;
  onSave?: () => void;
}

const EnhancedPresentationView: React.FC<EnhancedPresentationViewProps> = ({
  slides,
  template,
  title,
  onEdit,
  onDownload,
  onSave
}) => {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { toast } = useToast();

  const currentSlide = slides[currentSlideIndex];

  const nextSlide = () => {
    if (currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1);
    }
  };

  const goToSlide = (index: number) => {
    setCurrentSlideIndex(index);
  };

  const startFullscreenPresentation = () => {
    setIsFullscreen(true);
  };

  const closeFullscreenPresentation = () => {
    setIsFullscreen(false);
  };

  const savePresentation = async () => {
    try {
      if (onSave) {
        await onSave();
        toast({
          title: "Presentation Saved",
          description: "Your presentation has been saved successfully."
        });
      }
    } catch (error) {
      console.error('Failed to save presentation:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save presentation. Please try again.",
        variant: "destructive"
      });
    }
  };

  const sharePresentation = () => {
    if (navigator.share) {
      navigator.share({
        title: title,
        text: `Check out this presentation: ${title}`,
        url: window.location.href
      });
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: "Link Copied",
        description: "Presentation link copied to clipboard."
      });
    }
  };

  const renderSlideContent = (slide: ProcessedSlide) => {
    // Create a normalized slide object for SlideRenderer with proper template application
    const normalizedSlide = {
      id: slide.id,
      title: slide.title,
      content: Array.isArray(slide.content) ? slide.content.join('\n') : slide.content || '',
      type: slide.type || 'content',
      notes: slide.notes,
      imageUrl: slide.imageUrl,
      imageAlt: slide.imageAlt,
      template: template
    };

    return (
      <SlideRenderer
        slide={normalizedSlide}
        template={template}
        isEditing={false} data-id="otswylirz" data-path="src/components/ai/EnhancedPresentationView.tsx" />);


  };

  // Fullscreen presentation mode
  if (isFullscreen) {
    return (
      <FullscreenPresentationView
        slides={slides}
        template={template}
        title={title}
        onClose={closeFullscreenPresentation}
        autoplay={false} data-id="516pxqyeg" data-path="src/components/ai/EnhancedPresentationView.tsx" />);


  }

  return (
    <div className="max-w-7xl mx-auto p-6" data-id="wv6ksczv2" data-path="src/components/ai/EnhancedPresentationView.tsx">
      {/* Header */}
      <div className="flex items-center justify-between mb-6" data-id="t28jus9fa" data-path="src/components/ai/EnhancedPresentationView.tsx">
        <div data-id="q2o1c3xdb" data-path="src/components/ai/EnhancedPresentationView.tsx">
          <h1 className="text-3xl font-bold mb-2" data-id="fznx2iskq" data-path="src/components/ai/EnhancedPresentationView.tsx">{title}</h1>
          <div className="flex gap-2" data-id="vacuug9p8" data-path="src/components/ai/EnhancedPresentationView.tsx">
            <Badge variant="outline" data-id="33ga9anjx" data-path="src/components/ai/EnhancedPresentationView.tsx">{template.name}</Badge>
            <Badge variant="outline" data-id="9uc31wsqo" data-path="src/components/ai/EnhancedPresentationView.tsx">{slides.length} slides</Badge>
          </div>
        </div>
        <div className="flex gap-3" data-id="8xi3fmv1h" data-path="src/components/ai/EnhancedPresentationView.tsx">
          {onSave &&
          <Button onClick={savePresentation} variant="outline" data-id="iti64lfcs" data-path="src/components/ai/EnhancedPresentationView.tsx">
              <Save className="h-4 w-4 mr-2" data-id="mi8myyupr" data-path="src/components/ai/EnhancedPresentationView.tsx" />
              Save
            </Button>
          }
          {onEdit &&
          <Button onClick={onEdit} variant="outline" data-id="jl35ds455" data-path="src/components/ai/EnhancedPresentationView.tsx">
              <Edit3 className="h-4 w-4 mr-2" data-id="17gdjpa2i" data-path="src/components/ai/EnhancedPresentationView.tsx" />
              Edit Content
            </Button>
          }
          {onDownload &&
          <Button onClick={onDownload} variant="outline" data-id="eni1nbmxu" data-path="src/components/ai/EnhancedPresentationView.tsx">
              <Download className="h-4 w-4 mr-2" data-id="eje2nt9oj" data-path="src/components/ai/EnhancedPresentationView.tsx" />
              Export
            </Button>
          }
          <Button onClick={sharePresentation} variant="outline" data-id="yuqbo4voz" data-path="src/components/ai/EnhancedPresentationView.tsx">
            <Share2 className="h-4 w-4 mr-2" data-id="bqrdwweil" data-path="src/components/ai/EnhancedPresentationView.tsx" />
            Share
          </Button>
          <Button onClick={startFullscreenPresentation} data-id="hq9pci29r" data-path="src/components/ai/EnhancedPresentationView.tsx">
            <Presentation className="h-4 w-4 mr-2" data-id="5e4xs9elj" data-path="src/components/ai/EnhancedPresentationView.tsx" />
            Present
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="54gqxnwr6" data-path="src/components/ai/EnhancedPresentationView.tsx">
        {/* Slide Navigation */}
        <div className="lg:col-span-1" data-id="7zfolk5ng" data-path="src/components/ai/EnhancedPresentationView.tsx">
          <Card data-id="ap4pmb6re" data-path="src/components/ai/EnhancedPresentationView.tsx">
            <CardContent className="p-4" data-id="9djm1hlby" data-path="src/components/ai/EnhancedPresentationView.tsx">
              <h3 className="font-semibold mb-4" data-id="6gv60i65i" data-path="src/components/ai/EnhancedPresentationView.tsx">Slides</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto" data-id="q1g92esea" data-path="src/components/ai/EnhancedPresentationView.tsx">
                {slides.map((slide, index) =>
                <button
                  key={slide.id}
                  onClick={() => goToSlide(index)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  currentSlideIndex === index ?
                  'bg-blue-50 border-blue-200' :
                  'hover:bg-gray-50 border-gray-200'}`
                  } data-id="8mk9ovvim" data-path="src/components/ai/EnhancedPresentationView.tsx">

                    <div className="flex items-center gap-3" data-id="jygjstfv4" data-path="src/components/ai/EnhancedPresentationView.tsx">
                      <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-sm font-medium" data-id="er1pez7px" data-path="src/components/ai/EnhancedPresentationView.tsx">
                        {index + 1}
                      </div>
                      <div className="flex-grow min-w-0" data-id="pkek1lddd" data-path="src/components/ai/EnhancedPresentationView.tsx">
                        <p className="font-medium text-sm truncate" data-id="32r38w1we" data-path="src/components/ai/EnhancedPresentationView.tsx">{slide.title}</p>
                        <p className="text-xs text-gray-500" data-id="yj894p4v4" data-path="src/components/ai/EnhancedPresentationView.tsx">{slide.layoutType || slide.type}</p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Slide Display */}
        <div className="lg:col-span-3" data-id="iokfznc7z" data-path="src/components/ai/EnhancedPresentationView.tsx">
          <Card className="relative" data-id="9fbju16cx" data-path="src/components/ai/EnhancedPresentationView.tsx">
            <CardContent className="p-0" data-id="cwm7nyy4f" data-path="src/components/ai/EnhancedPresentationView.tsx">
              {/* Slide Content with proper aspect ratio */}
              <div className="aspect-video bg-white rounded-lg overflow-hidden border" data-id="vpetmn982" data-path="src/components/ai/EnhancedPresentationView.tsx">
                {renderSlideContent(currentSlide)}
              </div>

              {/* Slide Controls */}
              <div className="flex items-center justify-between p-4 border-t" data-id="x4p9gwdah" data-path="src/components/ai/EnhancedPresentationView.tsx">
                <div className="flex items-center gap-3" data-id="pv9kt0a2o" data-path="src/components/ai/EnhancedPresentationView.tsx">
                  <Button
                    onClick={prevSlide}
                    disabled={currentSlideIndex === 0}
                    variant="outline"
                    size="sm" data-id="a6kn8vhr4" data-path="src/components/ai/EnhancedPresentationView.tsx">

                    <ChevronLeft className="h-4 w-4" data-id="kbjvf0ne5" data-path="src/components/ai/EnhancedPresentationView.tsx" />
                  </Button>
                  <span className="text-sm text-gray-600" data-id="9ckypnpbc" data-path="src/components/ai/EnhancedPresentationView.tsx">
                    {currentSlideIndex + 1} of {slides.length}
                  </span>
                  <Button
                    onClick={nextSlide}
                    disabled={currentSlideIndex === slides.length - 1}
                    variant="outline"
                    size="sm" data-id="vp5uwrq87" data-path="src/components/ai/EnhancedPresentationView.tsx">

                    <ChevronRight className="h-4 w-4" data-id="0nny4fm5i" data-path="src/components/ai/EnhancedPresentationView.tsx" />
                  </Button>
                </div>

                <div className="flex gap-2" data-id="tqa3i2piv" data-path="src/components/ai/EnhancedPresentationView.tsx">
                  <Button variant="outline" size="sm" onClick={startFullscreenPresentation} data-id="96o9o8qgz" data-path="src/components/ai/EnhancedPresentationView.tsx">
                    <Play className="h-4 w-4 mr-2" data-id="y9ax13rr3" data-path="src/components/ai/EnhancedPresentationView.tsx" />
                    Slideshow
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={startFullscreenPresentation}
                    title="Present in Fullscreen" data-id="7j80rcr4t" data-path="src/components/ai/EnhancedPresentationView.tsx">

                    <Maximize2 className="h-4 w-4" data-id="g0ohak0ju" data-path="src/components/ai/EnhancedPresentationView.tsx" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Slide Details */}
          <Card className="mt-4" data-id="m5spqevw3" data-path="src/components/ai/EnhancedPresentationView.tsx">
            <CardContent className="p-4" data-id="1aqjp2pae" data-path="src/components/ai/EnhancedPresentationView.tsx">
              <div className="flex items-center gap-2 mb-3" data-id="vc3rfuz73" data-path="src/components/ai/EnhancedPresentationView.tsx">
                <Eye className="h-4 w-4 text-gray-500" data-id="m6ynbo0v7" data-path="src/components/ai/EnhancedPresentationView.tsx" />
                <span className="font-medium" data-id="vgcdp9b6i" data-path="src/components/ai/EnhancedPresentationView.tsx">Slide Details</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-id="snlv8js4u" data-path="src/components/ai/EnhancedPresentationView.tsx">
                <div data-id="qe2yarqg4" data-path="src/components/ai/EnhancedPresentationView.tsx">
                  <span className="text-gray-500" data-id="txnzdtl9h" data-path="src/components/ai/EnhancedPresentationView.tsx">Layout:</span>
                  <p className="font-medium" data-id="pq6zewjp2" data-path="src/components/ai/EnhancedPresentationView.tsx">{currentSlide.layoutType || currentSlide.type}</p>
                </div>
                <div data-id="tuoo1gcg5" data-path="src/components/ai/EnhancedPresentationView.tsx">
                  <span className="text-gray-500" data-id="5ejt8en9n" data-path="src/components/ai/EnhancedPresentationView.tsx">Content Type:</span>
                  <p className="font-medium" data-id="qlsoi0iik" data-path="src/components/ai/EnhancedPresentationView.tsx">{currentSlide.type}</p>
                </div>
                <div data-id="c8mug4acd" data-path="src/components/ai/EnhancedPresentationView.tsx">
                  <span className="text-gray-500" data-id="b6c0fjf2v" data-path="src/components/ai/EnhancedPresentationView.tsx">Template:</span>
                  <p className="font-medium" data-id="9vg6uem4d" data-path="src/components/ai/EnhancedPresentationView.tsx">{template.name}</p>
                </div>
              </div>
              {currentSlide.notes &&
              <div className="mt-4" data-id="nbpurw2dx" data-path="src/components/ai/EnhancedPresentationView.tsx">
                  <span className="text-gray-500" data-id="beepn90cj" data-path="src/components/ai/EnhancedPresentationView.tsx">Notes:</span>
                  <p className="text-sm mt-1" data-id="ixq7l9i5y" data-path="src/components/ai/EnhancedPresentationView.tsx">{currentSlide.notes}</p>
                </div>
              }
            </CardContent>
          </Card>
        </div>
      </div>
    </div>);

};

export default EnhancedPresentationView;